import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { api, mediaUrl } from '@/lib/api';
import { cmsService } from '@/services';
import { eventsService } from '@/services/events.service';
import { setCategories } from '@/lib/eventsMock';
import { PublicEventCard } from '@/components/public/PublicEventCard';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { PublicLayout } from '@/components/public/PublicLayout';
import { ExperienceFinder } from '@/components/public/ExperienceFinder';
import { ExperienceTicketCard } from '@/components/public/ExperienceTicketShowcase';
import { T } from '@/components/common/T';
import { ShieldCheck, LogIn } from 'lucide-react';
import { SilaBanner } from '@/components/public/SilaBanner';
import { SustainabilityMegaBanner } from '@/components/public/SustainabilityMegaBanner';
import { GoogleReviews } from '@/components/public/GoogleReviews';
import { investorsService } from '@/services/investors.service';
import { Reveal, RevealGroup, fadeUp } from '@/components/public/anim';
import {
  ArrowRight, MapPin, Hotel, UtensilsCrossed, ShoppingBag,
  Calendar, Leaf, Star, Mail, Compass, Handshake, Briefcase, TrendingUp,
} from 'lucide-react';

const HERO_SLIDES = [
  'https://images.pexels.com/photos/20450281/pexels-photo-20450281.jpeg?auto=compress&cs=tinysrgb&h=1200&w=1920',
  'https://images.pexels.com/photos/37406898/pexels-photo-37406898.jpeg?auto=compress&cs=tinysrgb&h=1200&w=1920',
  'https://images.pexels.com/photos/28127038/pexels-photo-28127038.jpeg?auto=compress&cs=tinysrgb&h=1200&w=1920',
  'https://images.pexels.com/photos/17983542/pexels-photo-17983542.jpeg?auto=compress&cs=tinysrgb&h=1200&w=1920',
];
const AREA_IMGS = [
  'https://images.pexels.com/photos/17983542/pexels-photo-17983542.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
  'https://images.pexels.com/photos/8006229/pexels-photo-8006229.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
  'https://images.pexels.com/photos/33343202/pexels-photo-33343202.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
];
const EXP_IMGS = [
  'https://images.pexels.com/photos/28127038/pexels-photo-28127038.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
  'https://images.pexels.com/photos/37406898/pexels-photo-37406898.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
  'https://images.pexels.com/photos/17983542/pexels-photo-17983542.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
  'https://images.pexels.com/photos/20450281/pexels-photo-20450281.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
];
const BLOG_IMGS = [
  'https://images.pexels.com/photos/33343202/pexels-photo-33343202.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
  'https://images.pexels.com/photos/8006229/pexels-photo-8006229.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
  'https://images.pexels.com/photos/37406898/pexels-photo-37406898.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
];
const AREA_ICONS = [Hotel, UtensilsCrossed, ShoppingBag];

const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

// srcset responsivo per immagini Pexels: mobile scarica il piccolo, i monitor
// grandi/retina fino a 2560px (piena nitidezza). No-op per URL non-Pexels (CMS).
const pexelsSrcSet = (url, widths = [768, 1280, 1920, 2560]) => {
  if (!url || !/images\.pexels\.com/.test(url)) return undefined;
  try {
    const base = url.replace(/([?&])dpr=\d+&?/, '$1').replace(/[?&]$/, '');
    return widths.map((w) => `${base.replace(/([?&])w=\d+/, `$1w=${w}`)} ${w}w`).join(', ');
  } catch (e) { return undefined; }
};

// Monta i figli solo quando entrano nel viewport (risparmia JS di terze parti
// sotto la piega). In prerender (no IntersectionObserver) mostra subito → SEO ok.
function WhenVisible({ children, rootMargin = '400px', minHeight = 300 }) {
  const holder = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') { setShow(true); return undefined; }
    const el = holder.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setShow(true); io.disconnect(); } });
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return <div ref={holder} style={show ? undefined : { minHeight }}>{show ? children : null}</div>;
}

const SectionHead = ({ eyebrow, title, subtitle, center }) => (
  <div className={center ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]">
      <Leaf className="h-3.5 w-3.5" />{eyebrow}
    </span>
    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
    {subtitle && <p className="mt-3 text-base leading-relaxed text-muted-foreground">{subtitle}</p>}
  </div>
);

function Hero({ cms = {} }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);
  const words = (str(cms.heroTitle) || t('hero.title')).split(' ');
  const stats = t('hero.stats', { returnObjects: true }) || [];

  useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 5200);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <section ref={ref} className="relative isolate flex min-h-[100svh] items-center overflow-hidden text-white" data-testid="home-hero">
      {/* Slideshow cinematografico */}
      <motion.div style={{ y: yImg }} className="absolute inset-0 z-0">
        {HERO_SLIDES.map((src, i) => (
          <div key={i} className="absolute inset-0 transition-opacity duration-[1600ms] ease-out" style={{ opacity: i === idx ? 1 : 0 }}>
            <img
              src={src}
              srcSet={pexelsSrcSet(src)}
              sizes="100vw"
              fetchPriority={i === 0 ? 'high' : undefined}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              alt=""
              aria-hidden="true"
              className={cn('h-full w-full object-cover', i === idx && !reduce && 'sila-kb')}
            />
          </div>
        ))}
      </motion.div>
      {/* Overlay scuro cinematografico + blend verso la pagina */}
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1]"
        aria-hidden="true"
        // eslint-disable-next-line react/forbid-dom-props
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, hsla(210,32%,6%,0.55) 0%, hsla(210,32%,6%,0.20) 32%, hsla(210,32%,6%,0.55) 72%, hsl(var(--background)) 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, hsla(210,32%,6%,0.72) 0%, hsla(210,32%,6%,0.30) 45%, transparent 72%)' }} />
      </motion.div>

      <div className="sila-section relative z-10 grid grid-cols-1 items-center gap-10 py-28 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-md">
            <Compass className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-sun))' }} />{str(cms.heroEyebrow) || t('hero.eyebrow')}
          </motion.span>

          <h1 className="mt-6 font-display text-5xl font-bold leading-[1.03] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.45)] sm:text-6xl lg:text-7xl">
            {words.map((w, i) => (
              <motion.span key={i} className="inline-block pr-[0.25em]"
                initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}>
                {w}
              </motion.span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/85 drop-shadow-[0_1px_10px_rgba(0,0,0,0.4)] sm:text-lg">{str(cms.heroSubtitle) || t('hero.subtitle')}</motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.62 }}
            className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="group rounded-full" data-testid="home-hero-primary-cta">
              <a href="#come-funziona" className="flex items-center">{str(cms.heroCtaPrimary) || t('hero.cta_primary')}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-white/10 text-white backdrop-blur-md hover:bg-white/20 hover:text-white" data-testid="home-hero-secondary-cta">
              <a href="#esperienze">{str(cms.heroCtaSecondary) || t('hero.cta_secondary')}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-white/10 text-white backdrop-blur-md hover:bg-white/20 hover:text-white" data-testid="home-hero-tertiary-cta">
              <Link to="/partner/registrati">{str(cms.heroCtaTertiary) || t('hero.cta_tertiary')}</Link>
            </Button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-12 flex flex-wrap gap-8">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="font-display text-3xl font-bold" style={{ color: 'hsl(var(--brand-sun))' }}>{s.value}</div>
                <div className="text-sm text-white/75">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Experience Finder — reattivo, agganciato alle logiche native */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5">
          <ExperienceFinder title={str(cms.finderTitle) || t('hero.finder_title')} />
        </motion.div>
      </div>

      {/* Slideshow dots */}
      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button key={i} aria-label={`Slide ${i + 1}`} onClick={() => setIdx(i)} data-testid={`hero-slide-dot-${i}`}
            className={cn('h-1.5 rounded-full transition-all duration-300', i === idx ? 'w-7 bg-white' : 'w-2.5 bg-white/45 hover:bg-white/70')} />
        ))}
      </div>
    </section>
  );
}

function JoinBoxes({ cms = {} }) {
  const boxes = [
    {
      key: 'partner',
      to: '/register',
      icon: Handshake,
      tag: str(cms.join1Tag) || 'Per le attività del territorio',
      title: str(cms.join1Title) || 'Diventa Stakeholder',
      desc: str(cms.join1Desc) || 'Hotel, ristoranti, artigiani, guide e produttori locali: gestisci la tua attività, ricevi prenotazioni e cresci sulla piattaforma.',
      cta: str(cms.join1Cta) || 'Registra la tua attività',
      img: 'https://images.pexels.com/photos/8006229/pexels-photo-8006229.jpeg?auto=compress&cs=tinysrgb&h=760&w=1100',
    },
    {
      key: 'investor',
      to: '/diventa-partner',
      icon: TrendingUp,
      tag: str(cms.join3Tag) || 'Per gli investitori',
      title: str(cms.join3Title) || 'Diventa Partner',
      desc: str(cms.join3Desc) || 'Sostieni lo sviluppo della Sila: area riservata con documenti, materiale ufficiale e la rendicontazione trasparente del tuo investimento.',
      cta: str(cms.join3Cta) || 'Scopri come partecipare',
      img: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&h=760&w=1100',
    },
    {
      key: 'lavora',
      to: '/lavora-con-noi',
      icon: Briefcase,
      tag: str(cms.join2Tag) || 'Per i professionisti',
      title: str(cms.join2Title) || 'Lavora con noi!',
      desc: str(cms.join2Desc) || 'Entra nel team come Staff o diventa Guida Sila Experiences: candidati e costruisci la tua carriera nella Sila.',
      cta: str(cms.join2Cta) || 'Scopri le opportunità',
      img: 'https://images.pexels.com/photos/28127038/pexels-photo-28127038.jpeg?auto=compress&cs=tinysrgb&h=760&w=1100',
    },
  ];
  return (
    <section className="sila-section py-14 sm:py-20" data-testid="home-join-boxes">
      <RevealGroup className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {boxes.map((b) => {
          const Icon = b.icon;
          return (
            <motion.div key={b.key} variants={fadeUp}>
              <Link
                to={b.to}
                data-testid={`home-join-${b.key}`}
                className="group relative flex min-h-[300px] flex-col justify-end overflow-hidden rounded-[28px] bg-[hsl(var(--brand-bark))] text-white shadow-[var(--shadow-md)] transition-transform duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:min-h-[340px]"
              >
                <img loading="lazy" src={b.img} srcSet={pexelsSrcSet(b.img)} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" alt={b.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform group-hover:scale-105" />
                <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(180deg, hsla(210,32%,6%,0.20) 0%, hsla(210,32%,6%,0.55) 55%, hsla(210,32%,6%,0.88) 100%)' }} />
                <div className="relative z-10 p-7 sm:p-9">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide backdrop-blur-md">
                    <Icon className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-sun))' }} />{b.tag}
                  </span>
                  <h3 className="mt-4 font-display text-3xl font-bold tracking-tight drop-shadow-[0_2px_14px_rgba(0,0,0,0.4)] sm:text-4xl">{b.title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-white/85 sm:text-base">{b.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold transition-transform duration-200 group-hover:translate-x-1" style={{ color: 'hsl(var(--brand-sun))' }}>
                    {b.cta}<ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </RevealGroup>
    </section>
  );
}

function Areas({ cms = {} }) {
  const { t } = useTranslation();
  const items = t('areas.items', { returnObjects: true }) || [];
  // Dati REALI dal DB (anche demo): conteggi + immagine di copertina per area.
  const [stats, setStats] = useState({ hotel: null, restaurants: null, shop: null });

  useEffect(() => {
    Promise.allSettled([
      api.get('/hotel/public/structures'),
      api.get('/restaurant/public/structures'),
      api.get('/ecommerce/public/shops'),
    ]).then(([h, r, sh]) => {
      const list = (res) => (res.status === 'fulfilled' && Array.isArray(res.value.data) ? res.value.data : []);
      const cover = (arr) => (arr.find((x) => x.cover_image) || {}).cover_image || '';
      const hotels = list(h);
      const rests = list(r);
      const shops = list(sh);
      const productTotal = shops.reduce((acc, x) => acc + (Number(x.product_count) || 0), 0);
      setStats({
        hotel: { count: hotels.length, cover: cover(hotels) },
        restaurants: { count: rests.length, cover: cover(rests) },
        shop: { count: shops.length, products: productTotal, cover: cover(shops) },
      });
    }).catch(() => { /* fallback silenzioso: restano le immagini di default */ });
  }, []);

  // Metadati per indice (ordine i18n: hotel, restaurants, shop)
  const meta = [
    { key: 'hotel', href: '/alloggi', data: stats.hotel, unit: (d) => `${d.count} ${d.count === 1 ? 'struttura' : 'strutture'}` },
    { key: 'restaurants', href: '/dove-mangiare', data: stats.restaurants, unit: (d) => `${d.count} ${d.count === 1 ? 'ristorante' : 'ristoranti'}` },
    { key: 'shop', href: '/shop', data: stats.shop, unit: (d) => (d.products > 0 ? `${d.products} prodotti` : `${d.count} ${d.count === 1 ? 'negozio' : 'negozi'}`) },
  ];

  return (
    <section id="aree" className="sila-section scroll-mt-20 py-16 sm:py-24">
      <Reveal><SectionHead eyebrow={str(cms.areasEyebrow) || t('areas.eyebrow')} title={str(cms.areasTitle) || t('areas.title')} subtitle={str(cms.areasSubtitle) || t('areas.subtitle')} /></Reveal>
      <RevealGroup className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {items.map((it, i) => {
          const Icon = AREA_ICONS[i] || Hotel;
          const m = meta[i] || {};
          const key = ['hotel', 'restaurants', 'shop'][i];
          const cover = m.data && m.data.cover ? mediaUrl(m.data.cover) : AREA_IMGS[i];
          const hasCount = m.data && typeof m.data.count === 'number';
          const clickable = !!m.href;

          const inner = (
            <Card className={cn(
              'group relative h-full overflow-hidden rounded-[28px] border-border bg-card shadow-[var(--shadow-sm)] transition-[box-shadow,transform] duration-300',
              clickable && 'hover:-translate-y-1 hover:shadow-[var(--shadow-md)]',
            )} data-testid={`home-areas-${key}-card`}>
              <div className="relative h-52 overflow-hidden">
                <img loading="lazy" src={cover} srcSet={pexelsSrcSet(cover)} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" alt={it.title}
                  className="h-full w-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform group-hover:scale-110"
                  onError={(e) => { e.currentTarget.src = AREA_IMGS[i]; }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, hsla(210,18%,8%,0.55) 100%)' }} />
                <Badge className="absolute left-4 top-4 rounded-full bg-background/85 text-foreground backdrop-blur">{it.tag}</Badge>
                {hasCount && (
                  <Badge className="absolute right-4 top-4 rounded-full bg-primary/90 text-primary-foreground backdrop-blur" data-testid={`home-areas-${key}-count`}>
                    {m.unit(m.data)}
                  </Badge>
                )}
                <span className="absolute bottom-4 left-4 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                  <Icon className="h-5 w-5" />
                </span>
              </div>
              <CardContent className="p-6">
                <h3 className="font-display text-xl font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{it.desc}</p>
                {clickable ? (
                  <div className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-primary transition-transform duration-200 group-hover:translate-x-1">
                    {t('areas.discover')} <ArrowRight className="h-4 w-4" />
                  </div>
                ) : (
                  <div className="mt-4 text-sm font-medium text-muted-foreground" data-testid={`home-areas-${key}-soon`}>Presto disponibile</div>
                )}
              </CardContent>
            </Card>
          );

          return (
            <motion.div key={i} variants={fadeUp}>
              {clickable
                ? <Link to={m.href} className="block h-full" data-testid={`home-areas-${key}-link`}>{inner}</Link>
                : inner}
            </motion.div>
          );
        })}
      </RevealGroup>
    </section>
  );
}

function Experiences({ cms = {} }) {
  const { t } = useTranslation();
  const mock = t('experiences.items', { returnObjects: true }) || [];
  const [exps, setExps] = useState(null);

  useEffect(() => {
    api.get('/experience/public/experiences')
      .then(({ data }) => setExps(Array.isArray(data?.experiences) ? data.experiences.slice(0, 4) : []))
      .catch(() => setExps([]));
  }, []);

  // Usa le esperienze REALI pubblicate dal CRM; fallback ai contenuti demo se non ce ne sono.
  const useReal = exps && exps.length > 0;

  const cards = useReal
    ? exps.map((e, i) => ({
        id: e.id,
        to: `/esperienze/${e.slug || e.id}`,
        img: e.cover_image ? mediaUrl(e.cover_image) : EXP_IMGS[i % EXP_IMGS.length],
        cat: e.category || 'Esperienza',
        title: e.title,
        place: (e.location && e.location.name) || e.owner_name || 'Sila',
        price: e.from_price,
      }))
    : mock.map((it, i) => ({
        to: '/esperienze/catalogo',
        img: EXP_IMGS[i % EXP_IMGS.length],
        cat: it.cat,
        title: it.title,
        place: it.place,
        date: it.date,
      }));

  return (
    <section id="esperienze" className="relative scroll-mt-20 overflow-hidden py-16 sm:py-24">
      <div className="pointer-events-none absolute inset-0 -z-10" style={{ background: 'radial-gradient(900px 480px at 15% 10%, hsla(var(--brand-lake)/0.14) 0%, transparent 60%), radial-gradient(760px 420px at 85% 30%, hsla(var(--brand-sun)/0.12) 0%, transparent 55%)' }} />
      <div className="sila-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHead eyebrow={str(cms.expEyebrow) || t('experiences.eyebrow')} title={str(cms.expTitle) || t('experiences.title')} subtitle={str(cms.expSubtitle) || t('experiences.subtitle')} />
            <Link to="/esperienze/catalogo" className="hidden items-center gap-1.5 text-sm font-semibold text-primary hover:underline sm:flex" data-testid="home-experiences-all"><T>Vedi tutte</T> <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </Reveal>
        <RevealGroup className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {useReal ? (
            exps.map((e, i) => (
              <motion.div key={e.id || i} variants={fadeUp} data-testid={`home-experience-card-${i}`}>
                <ExperienceTicketCard e={e} compact showFavorite />
              </motion.div>
            ))
          ) : (
            cards.map((c, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Link to={c.to} className="group block h-full" data-testid={`home-experience-card-${i}`}>
                  <Card className="h-full overflow-hidden rounded-[24px] border-border bg-card shadow-[var(--shadow-sm)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-md)]">
                    <div className="relative h-40 overflow-hidden">
                      <img loading="lazy" src={c.img} srcSet={pexelsSrcSet(c.img)} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" alt={c.title} onError={(e) => { e.currentTarget.src = EXP_IMGS[i % EXP_IMGS.length]; }} className="h-full w-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform group-hover:scale-110" />
                      {c.date ? (
                        <div className="absolute left-3 top-3 flex flex-col items-center rounded-xl bg-background/90 px-3 py-1.5 text-center shadow backdrop-blur">
                          <Calendar className="h-3.5 w-3.5 text-primary" />
                          <span className="mt-0.5 text-xs font-bold">{c.date}</span>
                        </div>
                      ) : null}
                    </div>
                    <CardContent className="p-5">
                      <Badge variant="secondary" className="rounded-full text-xs">{c.cat}</Badge>
                      <h3 className="mt-2 font-display text-lg font-semibold leading-snug line-clamp-1 transition-colors group-hover:text-primary">{c.title}</h3>
                      <p className="mt-1.5 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{c.place}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))
          )}
        </RevealGroup>
      </div>
    </section>
  );
}

const fmtDur = (min) => {
  const m = Number(min) || 0;
  if (!m) return '';
  if (m >= 60) { const h = Math.floor(m / 60); const r = m % 60; return r ? `${h}h ${r}m` : `${h}h`; }
  return `${m} min`;
};

// Card tour quadrata (Variante B): usata sia nella griglia sia nel carosello.
function TourCard({ t, i }) {
  const img = t.cover_image ? mediaUrl(t.cover_image) : EXP_IMGS[i % EXP_IMGS.length];
  const place = (t.location && t.location.name) || t.owner_name || 'Sila';
  const meta = [t.itinerary_count > 0 ? `${t.itinerary_count} tappe` : '', fmtDur(t.duration_min)].filter(Boolean).join(' · ');
  return (
    <Link to={`/esperienze/${t.slug || t.id}`}
      className="group relative block aspect-square overflow-hidden rounded-3xl ring-1 ring-border transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:shadow-2xl hover:ring-2 hover:ring-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <img loading="lazy" src={img} srcSet={pexelsSrcSet(img)} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" alt={t.title} onError={(e) => { e.currentTarget.src = EXP_IMGS[i % EXP_IMGS.length]; }}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" />
      <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 42%, hsla(210,32%,6%,0.38) 100%)' }} />
      <div className="absolute inset-x-3 bottom-3 rounded-2xl border border-white/25 bg-white/15 p-3 backdrop-blur-md">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate font-display text-base font-bold text-white drop-shadow">{t.title}</h3>
            <p className="flex items-center gap-1 truncate text-xs text-white/85"><MapPin className="h-3 w-3 shrink-0" /> {place}{meta ? ` · ${meta}` : ''}</p>
          </div>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform duration-200 ease-out group-hover:scale-110">
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
        {t.short_desc ? (
          <div className="max-h-0 overflow-hidden opacity-0 transition-all duration-300 ease-out group-hover:mt-2 group-hover:max-h-24 group-hover:opacity-100">
            <p className="line-clamp-3 text-xs text-white/90">{t.short_desc}</p>
          </div>
        ) : null}
      </div>
    </Link>
  );
}

// TOUR DEI BORGHI — griglia quadrata con barra "vetro" e reveal in hover (Variante B).
function Tours() {
  const [tours, setTours] = useState(null);
  useEffect(() => {
    api.get('/experience/public/experiences', { params: { type: 'tour' } })
      .then(({ data }) => setTours(Array.isArray(data?.experiences) ? data.experiences.slice(0, 12) : []))
      .catch(() => setTours([]));
  }, []);
  if (!tours || tours.length === 0) return null;
  const useCarousel = tours.length > 4;

  return (
    <section id="tour" className="sila-section scroll-mt-20 py-16 sm:py-24" data-testid="home-tours">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHead eyebrow="Tour dei Borghi" title="Itinerari da vivere" subtitle="Cammini, borghi e natura della Sila: percorsi guidati tappa dopo tappa." />
          <Link to="/esperienze/catalogo" className="hidden items-center gap-1.5 text-sm font-semibold text-primary hover:underline sm:flex" data-testid="home-tours-all"><T>Vedi tutti</T> <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </Reveal>

      {useCarousel ? (
        <Carousel opts={{ align: 'start' }} className="mt-12" data-testid="home-tours-carousel">
          <div className="mb-4 flex justify-end gap-2">
            <CarouselPrevious className="static translate-y-0" />
            <CarouselNext className="static translate-y-0" />
          </div>
          <CarouselContent className="-ml-5">
            {tours.map((t, i) => (
              <CarouselItem key={t.id || i} className="basis-4/5 pl-5 sm:basis-1/2 lg:basis-1/4" data-testid={`home-tour-card-${i}`}>
                <TourCard t={t} i={i} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      ) : (
        <RevealGroup className="mt-12 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {tours.map((t, i) => (
            <motion.div key={t.id || i} variants={fadeUp} data-testid={`home-tour-card-${i}`}>
              <TourCard t={t} i={i} />
            </motion.div>
          ))}
        </RevealGroup>
      )}
    </section>
  );
}

// EVENTI — biglietti verticali con strappo perforato (Variante B).
function Events() {
  const [events, setEvents] = useState(null);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState('tutte');
  useEffect(() => {
    eventsService.publicList()
      .then((data) => {
        setCategories(data?.categories || []);
        const ev = Array.isArray(data?.events) ? data.events : [];
        setEvents(ev);
        setCats((data?.categories || []).filter((c) => ev.some((e) => e.category === c.slug)));
      })
      .catch(() => setEvents([]));
  }, []);
  if (!events || events.length === 0) return null;

  const showFilters = cats.length > 1;
  const filtered = (active === 'tutte' ? events : events.filter((e) => e.category === active)).slice(0, 8);

  return (
    <section id="eventi" className="relative scroll-mt-20 overflow-hidden py-16 sm:py-24" data-testid="home-events">
      <div className="pointer-events-none absolute inset-0 -z-10" style={{ background: 'radial-gradient(820px 440px at 82% 12%, hsla(var(--brand-sun)/0.12) 0%, transparent 58%)' }} />
      <div className="sila-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHead eyebrow="Eventi in programma" title="Prendi il tuo biglietto" subtitle="Festival, sagre e serate speciali della Sila: scegli la data e acquista online." />
            <Link to="/eventi" className="hidden items-center gap-1.5 text-sm font-semibold text-primary hover:underline sm:flex" data-testid="home-events-all"><T>Vedi tutti</T> <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </Reveal>

        {showFilters && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="home-events-filters">
            <button type="button" onClick={() => setActive('tutte')} data-testid="home-events-filter-tutte"
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-150 ${active === 'tutte' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/70'}`}>
              Tutte
            </button>
            {cats.map((c) => (
              <button key={c.slug} type="button" onClick={() => setActive(c.slug)} data-testid={`home-events-filter-${c.slug}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-150 ${active === c.slug ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/70'}`}>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(var(${c.hsl_var || '--brand-pine'}))` }} />
                {c.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {filtered.map((e) => <PublicEventCard key={e.id || e.slug} e={e} />)}
        </div>
      </div>
    </section>
  );
}

function Blog({ cms = {} }) {
  const { t, i18n } = useTranslation();
  const mock = t('blog.items', { returnObjects: true }) || [];
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get('/blog/public/posts').then(({ data }) => setPosts(Array.isArray(data) ? data.slice(0, 3) : [])).catch(() => setPosts([]));
  }, []);

  // Usa i post reali dal CRM se disponibili, altrimenti fallback ai contenuti mock (nessun pezzo perso)
  const useReal = posts && posts.length > 0;
  const cards = useReal
    ? posts.map((p, i) => ({ to: `/blog/${p.slug}`, img: p.cover_image || BLOG_IMGS[i % BLOG_IMGS.length], cat: p.category?.name || 'Blog', title: p.title, excerpt: p.excerpt }))
    : mock.map((it, i) => ({ to: '/blog', img: BLOG_IMGS[i], cat: it.cat, title: it.title, excerpt: it.excerpt }));

  return (
    <section id="blog" className="sila-section scroll-mt-20 py-16 sm:py-24">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHead eyebrow={str(cms.blogEyebrow) || t('blog.eyebrow')} title={str(cms.blogTitle) || t('blog.title')} subtitle={str(cms.blogSubtitle) || t('blog.subtitle')} />
          <Link to="/blog" className="hidden items-center gap-1.5 text-sm font-semibold text-primary hover:underline sm:flex" data-testid="home-blog-all">{i18n.language === 'en' ? 'All articles' : 'Tutti gli articoli'} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </Reveal>
      <RevealGroup className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3">
        {cards.map((c, i) => (
          <motion.div key={i} variants={fadeUp}>
            <Link to={c.to} className="group block" data-testid={`home-blog-card-${i}`}>
              <div className="relative h-56 overflow-hidden rounded-[24px] shadow-[var(--shadow-sm)]">
                <img loading="lazy" src={c.img} srcSet={pexelsSrcSet(c.img)} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" alt={c.title} className="h-full w-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform group-hover:scale-110" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <Badge className="absolute left-4 top-4 rounded-full bg-background/85 text-foreground backdrop-blur">{c.cat}</Badge>
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold leading-snug transition-colors group-hover:text-primary">{c.title}</h3>
              <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{c.excerpt}</p>
              <div className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-primary">
                {t('blog.read')} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </div>
            </Link>
          </motion.div>
        ))}
      </RevealGroup>
    </section>
  );
}

function Newsletter({ cms = {} }) {
  const { t } = useTranslation();
  return (
    <section id="newsletter" className="sila-section py-16 sm:py-24">
      <Reveal>
        <div className="grain-overlay relative overflow-hidden rounded-[32px] bg-primary px-6 py-14 text-primary-foreground sm:px-14">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[hsl(var(--brand-sun)/0.25)] blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-[hsl(var(--brand-lake)/0.25)] blur-2xl" />
          <div className="relative mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
              <Star className="h-3.5 w-3.5" />Newsletter
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">{str(cms.nlTitle) || t('newsletter.title')}</h2>
            <p className="mt-3 text-base leading-relaxed text-primary-foreground/85">{str(cms.nlSubtitle) || t('newsletter.subtitle')}</p>
            <form className="mx-auto mt-7 flex max-w-md flex-col gap-3 sm:flex-row" onSubmit={(e) => e.preventDefault()}>
              <div className="relative flex-1">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input type="email" required placeholder={t('newsletter.placeholder')} aria-label={t('newsletter.placeholder')} className="h-11 bg-background pl-9 text-foreground" data-testid="footer-newsletter-email-input" />
              </div>
              <Button type="submit" size="lg" variant="secondary" className="rounded-full" data-testid="footer-newsletter-submit-button">{t('newsletter.cta')}</Button>
            </form>
            <p className="mt-3 text-xs text-primary-foreground/70">{t('newsletter.privacy')}</p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

// Striscia loghi partner: visibile SOLO se almeno un partner ha caricato il logo.
// Se non ci sono loghi, non renderizza nulla (la Home resta invariata).
function PartnersStrip() {
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    let alive = true;
    investorsService.publicPartners()
      .then((r) => { if (alive) setPartners((r && r.partners) || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!partners.length) return null;
  return (
    <section className="border-t border-border bg-muted/30 py-14" data-testid="home-partners">
      <div className="mx-auto max-w-6xl px-4">
        <p className="mb-8 text-center text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">I nostri partner</p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-8">
          {partners.map((p, i) => {
            const logo = (
              <img
                src={p.logo} alt={p.name} title={p.name} loading="lazy" decoding="async"
                className="h-12 w-auto max-w-[160px] object-contain opacity-70 grayscale transition-all duration-200 hover:opacity-100 hover:grayscale-0 sm:h-14"
              />
            );
            return p.website ? (
              <a key={i} href={p.website} target="_blank" rel="noopener noreferrer" aria-label={p.name} data-testid={`home-partner-${i}`}>{logo}</a>
            ) : (
              <span key={i} data-testid={`home-partner-${i}`}>{logo}</span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const AboutAndLogin = () => (
  <section className="sila-section py-14 sm:py-20" data-testid="home-about-login">
    <div className="mx-auto max-w-5xl rounded-2xl border border-border bg-muted/30 p-6 sm:p-10">
      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="home-about-title">
            Cos'è Sila Experiences
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Sila Experiences è la piattaforma che collega i viaggiatori con esperienze, soggiorni,
            tour, eventi, ristoranti e servizi degli operatori locali della Sila. Puoi esplorare e
            prenotare liberamente senza alcun account: la navigazione del sito e delle pagine
            pubbliche non richiede l'accesso.
          </p>
        </div>
        <div>
          <h3 className="flex items-center gap-2 font-display text-xl font-semibold" data-testid="home-google-title">
            <LogIn className="h-5 w-5 text-primary" />Perché usiamo l'accesso con Google
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            L'accesso con Google è offerto solo come modo rapido e sicuro per entrare nell'area
            personale (clienti) o nell'area operatori, senza creare una password dedicata. Usiamo
            esclusivamente nome, email e foto del profilo per autenticarti e gestire il tuo account;
            non accediamo ad altri dati o servizi del tuo account Google.
          </p>
          <Link
            to="/privacy-sicurezza"
            data-testid="home-privacy-link"
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ShieldCheck className="h-4 w-4" />Privacy &amp; Sicurezza
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  const [cms, setCms] = useState({});

  useEffect(() => {
    let alive = true;
    cmsService.getPageContent('home').then((c) => { if (alive) setCms(c || {}); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <PublicLayout>
      <Seo path="/" />
      <Hero cms={cms} />
      <SilaBanner />
      <Areas cms={cms} />
      <SustainabilityMegaBanner />
      <Experiences cms={cms} />
      <Tours />
      <Events />
      <Blog cms={cms} />
      <Newsletter cms={cms} />
      <JoinBoxes cms={cms} />
      <WhenVisible rootMargin="500px" minHeight={360}>
        <GoogleReviews />
      </WhenVisible>
      <PartnersStrip />
      <AboutAndLogin />
    </PublicLayout>
  );
}
